import { useState } from "react";
import { Link } from "react-router-dom";
import { useCompanies } from "@/hooks/useCompanies";
import { useCompanyFinancials } from "@/hooks/useCompanyFinancials";
import { useCreateShareReport } from "@/hooks/useShareReport";
import ReportView from "@/components/ReportView";
import ShareReportDialog from "@/components/ShareReportDialog";
import { currentBrand } from "@/lib/brand";

/**
 * User-level dashboard. v1 picks the first company; switcher comes when a user
 * actually has more than one. Share button creates a public snapshot of that
 * same company (per-domain branded via source_site_id captured on signup).
 */
export default function Dashboard() {
  const companies = useCompanies();
  const primary = companies.data?.[0];
  const financials = useCompanyFinancials(primary?.id);
  const brand = currentBrand();

  const [shareOpen, setShareOpen] = useState(false);
  const share = useCreateShareReport();

  if (companies.isLoading) return <p className="text-muted-foreground">Loading…</p>;

  if (!primary) {
    return (
      <div className="rounded-lg border p-10 text-center">
        <h1 className="text-2xl font-semibold">Welcome to {brand.name}</h1>
        <p className="mt-2 text-muted-foreground">
          Add your company to start building your dashboard.
        </p>
        <Link
          to="/dashboard/app/companies/new"
          className="mt-6 inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Add a company
        </Link>
      </div>
    );
  }

  const canShare = (financials.data?.length ?? 0) > 0;

  return (
    <>
      <div className="mb-6 flex justify-end gap-2">
        <Link
          to={`/dashboard/app/companies/${primary.id}`}
          className="rounded-md border px-3 py-2 text-sm font-medium hover:bg-secondary"
        >
          Manage company
        </Link>
        <button
          type="button"
          onClick={() => {
            setShareOpen(true);
            share.reset();
            share.mutate(primary.id);
          }}
          disabled={share.isPending || !canShare}
          title={
            canShare
              ? "Generate a public read-only link to this dashboard"
              : "Sync FreeAgent first so there's something to share"
          }
          className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {share.isPending ? "Generating…" : "Share dashboard"}
        </button>
      </div>

      <ReportView company={primary} financials={financials.data ?? []} />

      <ShareReportDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        token={share.data?.token ?? null}
        expiresAt={share.data?.expires_at ?? null}
        isPending={share.isPending}
        errorMessage={
          share.isError
            ? ((share.error as Error)?.message ?? "Failed to generate share link.")
            : null
        }
      />
    </>
  );
}
